import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectedOptimistic, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Listen for slideshow select events to update thumbnail active states
    // Use requestAnimationFrame to ensure slideshow is fully initialized
    requestAnimationFrame(() => {
      if (this.refs.slideshow) {
        this.refs.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
        // Set initial active state
        const currentIndex = this.refs.slideshow.current ?? 0;
        this.#updateThumbnailActiveState(currentIndex);
      }
    });

    // Check if there's a variant in the URL and reorder on initial load
    this.#handleInitialVariantFromUrl();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Optimistically swaps the main image when a color swatch is selected on PDP.
   * Uses product JSON (cached) to get the variant's featured image and updates the visible slide immediately.
   */
  #handleVariantSelectedOptimistic = async (event) => {
    if (!(event instanceof VariantSelectedEvent)) return;
    const variantId = event.detail?.resource?.variantId;
    if (!variantId) return;
    const picker = event.target;
    if (!picker?.closest || picker.closest('product-card') || picker.closest('quick-add-dialog')) return;

    const productUrl = picker.dataset?.productUrl;
    if (!productUrl || !/\/products\//.test(productUrl)) return;

    const VariantPickerClass = customElements.get('variant-picker')?.constructor;
    const getProductJson = VariantPickerClass?.getProductJson;
    if (typeof getProductJson !== 'function') return;

    const product = await getProductJson(productUrl);
    if (!product?.variants) return;
    const variant = product.variants.find((v) => String(v.id) === String(variantId));
    const featuredMedia = variant?.featured_media;
    const imgSrc = featuredMedia?.preview_image?.src || featuredMedia?.src;
    if (!imgSrc) return;

    const slide = this.querySelector('slideshow-slide[aria-hidden="false"], slideshow-slide:not([aria-hidden="true"])');
    const mainImg = slide?.querySelector?.('img.product-media__image') || this.querySelector('img.product-media__image');
    if (mainImg instanceof HTMLImageElement) {
      const highRes = imgSrc.includes('?') ? `${imgSrc}&width=1200` : `${imgSrc}?width=1200`;
      mainImg.src = highRes;
      mainImg.srcset = `${highRes} 1x`;
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
     
    if (!newMediaGallery) return;
    
    // Get the selected color swatch value from the variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker && newMediaGallery instanceof HTMLElement) {
      const colorSwatch = variantPicker.querySelector('input[type="radio"]:checked');
      if (colorSwatch instanceof HTMLInputElement && colorSwatch.value) {
        const swatchValue = colorSwatch.value.trim();
        
        // Reorder thumbnails and slides based on data-variant-title
        this.#reorderByVariantTitle(newMediaGallery, swatchValue);
      }
    }
    
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles initial variant from URL on page load.
   */
  #handleInitialVariantFromUrl() {
    // Wait for DOM to be ready and variant picker to be initialized
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        requestAnimationFrame(() => this.#applyInitialVariantReordering());
      });
    } else {
      // Use requestAnimationFrame to ensure variant picker is initialized
      requestAnimationFrame(() => {
        setTimeout(() => this.#applyInitialVariantReordering(), 100);
      });
    }
  }

  /**
   * Applies reordering based on the selected variant from URL or initial selection.
   */
  #applyInitialVariantReordering() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    // Get the selected color swatch value
    const colorSwatch = variantPicker.querySelector('input[type="radio"]:checked');
    if (!(colorSwatch instanceof HTMLInputElement) || !colorSwatch.value) return;

    const swatchValue = colorSwatch.value.trim();
    if (!swatchValue) return;

    // Apply reordering to the current gallery
    this.#reorderByVariantTitle(this, swatchValue);
  }

  /**
   * Filters thumbnails and slides to show only those matching the selected variant.
   * Hides non-matching thumbnails and slides based on data-variant-title.
   *
   * @param {HTMLElement} mediaGallery - The media gallery element to filter.
   * @param {string} swatchValue - The selected swatch value to match against.
   */
  #reorderByVariantTitle(mediaGallery, swatchValue) {
    // Find the thumbnails container div (not the slideshow-controls element)
    const thumbnailsWrapper = mediaGallery.querySelector('.slideshow-controls__thumbnails');
    if (!thumbnailsWrapper || !(thumbnailsWrapper instanceof HTMLElement)) return;
    
    const thumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
    if (thumbnails.length === 0) return;
    
    // Find all slides
    const slidesContainer = mediaGallery.querySelector('slideshow-slides');
    if (!slidesContainer || !(slidesContainer instanceof HTMLElement)) return;
    
    const slides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
    if (slides.length === 0) return;
    
    // First, show all thumbnails and slides (in case a previous selection hid some)
    thumbnails.forEach((thumbnail) => {
      if (thumbnail instanceof HTMLElement) {
        thumbnail.style.removeProperty('display');
        thumbnail.removeAttribute('hidden');
      }
    });
    
    slides.forEach((slide) => {
      if (slide instanceof HTMLElement) {
        slide.style.removeProperty('display');
        slide.removeAttribute('hidden');
      }
    });
    
    // Track which indices match
    const matchingIndices = new Set();
    
    // Normalize swatch value for comparison (case-insensitive, trim)
    const normalizedSwatchValue = swatchValue.toLowerCase().trim();
    
    // First, check all thumbnails and slides for data-variant-title matches
    const slidesWithoutVariantTitle = new Set();
    
    thumbnails.forEach((thumbnail, index) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      const variantTitle = thumbnail.getAttribute('data-variant-title');
      
      if (!variantTitle || variantTitle.trim() === '') {
        // Slides without variant title should always be shown
        slidesWithoutVariantTitle.add(index);
        matchingIndices.add(index);
      } else {
        const normalizedVariantTitle = variantTitle.trim().toLowerCase();
        if (normalizedVariantTitle === normalizedSwatchValue) {
          matchingIndices.add(index);
        }
      }
    });
    
    // Also check slides themselves for data-variant-title
    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      const slideVariantTitle = slide.getAttribute('data-variant-title');
      
      if (!slideVariantTitle || slideVariantTitle.trim() === '') {
        // Slides without variant title should always be shown
        slidesWithoutVariantTitle.add(index);
        matchingIndices.add(index);
      } else {
        const normalizedSlideTitle = slideVariantTitle.trim().toLowerCase();
        if (normalizedSlideTitle === normalizedSwatchValue) {
          matchingIndices.add(index);
        }
      }
    });
    
    // Only filter if we have explicit matches - otherwise show all slides
    // Also, always show slides that don't have a data-variant-title
    if (matchingIndices.size > 0) {
      // We have matches, so filter to show only matching ones (plus any without variant title)
      thumbnails.forEach((thumbnail, index) => {
        if (!(thumbnail instanceof HTMLElement)) return;
        
        if (matchingIndices.has(index)) {
          thumbnail.style.removeProperty('display');
          thumbnail.removeAttribute('hidden');
        } else {
          thumbnail.style.display = 'none';
          thumbnail.setAttribute('hidden', 'true');
        }
      });
      
      slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        
        if (matchingIndices.has(index)) {
          slide.style.removeProperty('display');
          slide.removeAttribute('hidden');
        } else {
          slide.style.display = 'none';
          slide.setAttribute('hidden', 'true');
        }
      });
    } else {
      // No matches found - show all slides (don't filter)
      thumbnails.forEach((thumbnail) => {
        if (thumbnail instanceof HTMLElement) {
          thumbnail.style.removeProperty('display');
          thumbnail.removeAttribute('hidden');
        }
      });
      slides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.style.removeProperty('display');
          slide.removeAttribute('hidden');
        }
      });
      // Reset matchingIndices to include all for proper indexing
      thumbnails.forEach((_, index) => matchingIndices.add(index));
    }
    
    // Get only visible (matching) thumbnails and slides
    const visibleThumbnails = thumbnails.filter((thumb, index) => matchingIndices.has(index));
    const visibleSlides = slides.filter((slide, index) => matchingIndices.has(index));
    
    // Update indices for visible thumbnails
    visibleThumbnails.forEach((thumbnail, newIndex) => {
      if (thumbnail instanceof HTMLElement) {
        // Update the on:click attribute to use the new index (only counting visible ones)
        thumbnail.setAttribute('on:click', `/select/${newIndex}`);
        
        // Update aria-label to reflect new position
        const totalCount = visibleThumbnails.length;
        thumbnail.setAttribute('aria-label', `Slide ${newIndex + 1} of ${totalCount}`);
        
        // Update aria-selected and active class (first visible thumbnail should be selected)
        if (newIndex === 0) {
          thumbnail.setAttribute('aria-selected', 'true');
          thumbnail.classList.add('active');
        } else {
          thumbnail.setAttribute('aria-selected', 'false');
          thumbnail.classList.remove('active');
        }
      }
    });
    
    // Update slides: first visible slide should be visible, others hidden
    visibleSlides.forEach((slide, newIndex) => {
      if (slide instanceof HTMLElement) {
        // Update aria-hidden on slides (first should be visible)
        slide.setAttribute('aria-hidden', newIndex === 0 ? 'false' : 'true');
      }
    });
    
    // If there's a slideshow component, select the first visible slide
    if (this.slideshow && visibleSlides.length > 0) {
      requestAnimationFrame(() => {
        // Find the first visible slide's index in the slideshow's slides array
        const firstVisibleSlide = visibleSlides[0];
        if (firstVisibleSlide instanceof HTMLElement && this.slideshow?.slides) {
          const firstVisibleIndex = this.slideshow.slides.indexOf(firstVisibleSlide);
          if (firstVisibleIndex !== -1 && this.slideshow) {
            // Select the first visible slide using its index in the slideshow array
            this.slideshow.select(firstVisibleIndex, undefined, { animate: false });
            // Update thumbnail active state - use the slideshow index
            this.#updateThumbnailActiveState(firstVisibleIndex);
          }
        }
      });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow select events to update thumbnail active states.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const { index, slide } = event.detail;
    // Use the slide element to find the corresponding thumbnail
    this.#updateThumbnailActiveStateBySlide(slide, index);
  };

  /**
   * Updates the active state of thumbnails based on the current slide element.
   * Matches thumbnails to slides by their index position.
   * @param {Element} currentSlide - The currently selected slide element.
   * @param {number} currentIndex - The current slide index from slideshow.
   */
  #updateThumbnailActiveStateBySlide(currentSlide, currentIndex) {
    const thumbnailsWrapper = this.querySelector('.slideshow-controls__thumbnails');
    if (!thumbnailsWrapper) return;

    // Get all thumbnails
    const allThumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
    
    // Get all slides to find the slide's index in the DOM
    const slidesContainer = this.querySelector('slideshow-slides');
    const allSlides = slidesContainer ? Array.from(slidesContainer.querySelectorAll('slideshow-slide')) : [];
    
    // Find the index of the current slide in the DOM (this is the thumbnail index)
    let slideIndex = currentIndex;
    if (currentSlide && allSlides.length > 0) {
      const foundIndex = allSlides.indexOf(currentSlide);
      if (foundIndex !== -1) {
        slideIndex = foundIndex;
      }
    }
    
    // Update all thumbnails - match by index
    allThumbnails.forEach((thumbnail, thumbnailIndex) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      
      // Skip hidden thumbnails but still remove active state
      if (thumbnail.style.display === 'none' || thumbnail.hasAttribute('hidden')) {
        thumbnail.classList.remove('active');
        thumbnail.setAttribute('aria-selected', 'false');
        return;
      }
      
      // Match thumbnail index to slide index
      const isActive = thumbnailIndex === slideIndex;
      
      // Apply active state
      if (isActive) {
        thumbnail.classList.add('active');
        thumbnail.setAttribute('aria-selected', 'true');
      } else {
        thumbnail.classList.remove('active');
        thumbnail.setAttribute('aria-selected', 'false');
      }
    });
  }

  /**
   * Updates the active state of thumbnails based on the current slide index.
   * Only updates visible (non-hidden) thumbnails.
   * @param {number} currentIndex - The current slide index from the slideshow (in the slideshow's slides array).
   */
  #updateThumbnailActiveState(currentIndex) {
    // Get the current slide from the slideshow
    if (this.slideshow?.slides && this.slideshow.slides[currentIndex]) {
      const currentSlide = this.slideshow.slides[currentIndex];
      this.#updateThumbnailActiveStateBySlide(currentSlide, currentIndex);
    } else {
      // Fallback: update by index only
      const thumbnailsWrapper = this.querySelector('.slideshow-controls__thumbnails');
      if (!thumbnailsWrapper) return;

      const allThumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
      
      allThumbnails.forEach((thumbnail, thumbnailIndex) => {
        if (!(thumbnail instanceof HTMLElement)) return;
        
        // Skip hidden thumbnails
        if (thumbnail.style.display === 'none' || thumbnail.hasAttribute('hidden')) {
          thumbnail.classList.remove('active');
          thumbnail.setAttribute('aria-selected', 'false');
          return;
        }
        
        const isActive = thumbnailIndex === currentIndex;
        
        if (isActive) {
          thumbnail.classList.add('active');
          thumbnail.setAttribute('aria-selected', 'true');
        } else {
          thumbnail.classList.remove('active');
          thumbnail.setAttribute('aria-selected', 'false');
        }
      });
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
